import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useHospitalOnboarding } from '../../contexts/HospitalOnboardingContext';
import OnboardingHeader from '../../components/hospitalOnboarding/OnboardingHeader';
import OnboardingStepProgressBar from '../../components/hospitalOnboarding/OnboardingStepProgressBar';
import Step1HospitalProfile from './Step1HospitalProfile';
import Step2FacilitiesContacts from './Step2FacilitiesContacts';
import Step3AmbulanceRequirements from './Step3AmbulanceRequirements';
import Step4AreasDestinations from './Step4AreasDestinations';
import Step5ReviewSubmit from './Step5ReviewSubmit';

// Fleet Multi-step Onboarding Components
import FleetOnboardingStepProgressBar from './fleet/FleetOnboardingStepProgressBar';
import Step1FleetOwnerProfile from './fleet/Step1FleetOwnerProfile';
import Step2CapacityCoverage from './fleet/Step2CapacityCoverage';
import Step3RidesClients from './fleet/Step3RidesClients';
import Step4Rates from './fleet/Step4Rates';
import Step5AddAmbulances from './fleet/Step5AddAmbulances';
import Step6FleetReviewSubmit from './fleet/Step6FleetReviewSubmit';

export const HospitalOnboardingLayout = ({
  isEmbedded = false,
  partnerTypeOverride = null,
  applicationIdOverride = null,
  onBack = null,
  onSubmitted = null
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentStep, setCurrentStep, formData, updateFields, applicationId, showToast } = useHospitalOnboarding();

  const urlType = searchParams.get('type');
  const urlStep = searchParams.get('step');
  const isFleet =
    partnerTypeOverride === 'fleet' ||
    urlType === 'fleet' ||
    formData.partnerType === 'fleet' ||
    localStorage.getItem('risa_partner_type') === 'fleet';
  const partnerType = isFleet ? 'fleet' : 'hospital';

  const urlAppId = searchParams.get('applicationId');
  const storedAppId = typeof localStorage !== 'undefined' ? localStorage.getItem('risa_current_application_id') : null;
  const rawAppId =
    applicationIdOverride ||
    urlAppId ||
    applicationId ||
    storedAppId ||
    formData.application_id;

  const isValidAppId =
    typeof rawAppId === 'string' &&
    rawAppId.trim().length > 0 &&
    !rawAppId.startsWith('RISA-APP-');

  const activeAppId = isValidAppId ? rawAppId : null;
  const isRegistered = isEmbedded ? true : !!activeAppId;

  // Application Guard: Manual onboarding wizard requires prior partner registration
  useEffect(() => {
    if (isEmbedded) return;
    if (!isRegistered) {
      if (showToast) {
        showToast('Please complete initial partner registration first before onboarding.', 'error');
      }
      navigate(`/hospital/register?type=${partnerType}`, { replace: true });
    }
  }, [isEmbedded, isRegistered, partnerType, navigate, showToast]);

  // Sync partnerType if URL explicitly specifies it
  useEffect(() => {
    if (urlType && urlType !== formData.partnerType) {
      updateFields({ partnerType: urlType });
      localStorage.setItem('risa_partner_type', urlType);
    }
  }, [urlType, formData.partnerType, updateFields]);

  // Sync step if URL explicitly specifies it
  useEffect(() => {
    if (urlStep) {
      const stepNum = parseInt(urlStep, 10);
      if (!isNaN(stepNum) && stepNum >= 1 && stepNum <= (isFleet ? 6 : 5)) {
        setCurrentStep(stepNum);
      }
    }
  }, [urlStep, isFleet, setCurrentStep]);

  const renderCurrentStep = () => {
    if (isFleet) {
      switch (currentStep) {
        case 1:
          return <Step1FleetOwnerProfile />;
        case 2:
          return <Step2CapacityCoverage />;
        case 3:
          return <Step3RidesClients />;
        case 4:
          return <Step4Rates />;
        case 5:
          return <Step5AddAmbulances />;
        case 6:
          return <Step6FleetReviewSubmit />;
        default:
          return <Step1FleetOwnerProfile />;
      }
    }

    // Default: Hospital Multi-Step Onboarding (5 steps)
    switch (currentStep) {
      case 1:
        return <Step1HospitalProfile />;
      case 2:
        return <Step2FacilitiesContacts />;
      case 3:
        return <Step3AmbulanceRequirements />;
      case 4:
        return <Step4AreasDestinations />;
      case 5:
        return <Step5ReviewSubmit />;
      default:
        return <Step1HospitalProfile />;
    }
  };

  if (isEmbedded) {
    return (
      <div className="w-full">
        {/* Top bar inside Command Center with Back button and Status */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 mb-5 border-b border-slate-200">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 transition shadow-2xs cursor-pointer self-start"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-slate-600" />
            <span>Back to Onboarding Requests</span>
          </button>

          <div className="flex items-center gap-2.5">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              {isFleet ? 'Ambulance Fleet Onboarding' : 'Hospital Onboarding'}
            </span>
            {formData?.application_code && (
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 font-mono">
                {String(formData.application_code).replace(/\D/g, '') || formData.application_code}
              </span>
            )}
          </div>
        </div>

        {/* Stepper Progress Bar (Fleet: 6 pills | Hospital: 5 pills) */}
        {isFleet ? <FleetOnboardingStepProgressBar /> : <OnboardingStepProgressBar />}

        {/* Dynamic Step View */}
        <div className="animate-fade-in">
          {renderCurrentStep()}
        </div>
      </div>
    );
  }

  if (!isRegistered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center p-6 bg-white rounded-2xl shadow-sm border border-slate-200">
          <p className="text-sm font-medium text-slate-700">Please complete initial partner registration first. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col font-sans"
      style={{
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
      }}
    >
      {/* Top Header */}
      <OnboardingHeader />

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Stepper Progress Bar (Fleet: 6 pills | Hospital: 5 pills) */}
        {isFleet ? <FleetOnboardingStepProgressBar /> : <OnboardingStepProgressBar />}

        {/* Dynamic Step View */}
        <div className="animate-fade-in">
          {renderCurrentStep()}
        </div>
      </main>

      {/* Minimal Footer */}
      <footer className="py-5 text-center text-[11px] text-gray-400 border-t border-gray-200/60 bg-white">
        © {new Date().getFullYear()} RISA Smart Ambulance Platform. All rights reserved.
      </footer>
    </div>
  );
};

export default HospitalOnboardingLayout;
